import { format } from 'date-fns';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { Dimensions, Platform, StyleSheet } from 'react-native';

import { Box, Text } from '@components/Restyle';
import { ActivityLogMobile } from '@screens/Projects/Report/components/ActivityLogMobile';
import {
  getProjectActivityMessage,
  ProjectActivityType,
} from '@screens/Projects/Report/TaskDetail';

export interface ProjectActivityLogType {
  __typename: string;
  createdAt: Date;
  event: ProjectActivityType;
  id: string;
  user: User;
  attachment: Attachment;
}

export interface Attachment {
  __typename: string;
  id: string;
  chatId: string;
  body: string;
  clientId: string;
  createdAt: Date;
  publishedAt: Date;
  authorId: string;
  author: Author;
  entityTask?: {
    members?: Array<{
      role?: string;
      user?: { id?: string };
    }>;
  };
}

export interface Author {
  __typename: string;
  id: string;
  name: string;
  firstName: string;
  lastName: string;
  createdAt: Date;
}

export interface User {
  __typename: string;
  id: string;
  name: string;
  firstName: string;
  lastName: string;
}

const getRole = (log?: {
  attachment?: {
    entityTask?: { members?: Array<{ user?: { id?: string }; role?: string }> };
  };
  user?: { id?: string };
}) => {
  const role =
    log?.attachment?.entityTask?.members
      ?.find((member) => member?.user?.id === log?.user?.id)
      ?.role?.toLowerCase() || '';
  return role;
};

const ActivityLog = ({
  data,
}: {
  data: Array<ProjectActivityLogType> | undefined;
}) => {
  return data?.map((item) => {
    const role = getRole(item);
    if (item?.attachment?.body === '') {
      return;
    }
    return (
      <Box key={item?.id}>
        {Platform.OS === 'web' ? (
          <Box marginHorizontal='l'>
            <Box style={styles.dataRow}>
              <Box style={styles.memberColumn}>
                <Box flexDirection='row' alignItems='center'>
                  <Box style={styles.circle} />
                  <Text
                    variant='body'
                    color='textPrimary'
                    fontWeight='700'
                    fontSize={16}
                    marginHorizontal='s'
                    style={styles.nameText}
                    numberOfLines={2}>
                    {item?.user?.name ?? ''}
                  </Text>
                </Box>
              </Box>
              <Box style={styles.dateColumn}>
                <Text
                  variant='body'
                  color='textPrimary'
                  fontWeight='400'
                  fontSize={16}>
                  {format(
                    item?.createdAt || '2025-03-12T05:30:00+05:30',
                    'MMM dd, yyyy'
                  )}
                </Text>
              </Box>
              <Box style={styles.activityColumn}>
                <Text
                  variant='body'
                  color='textPrimary'
                  fontWeight='400'
                  fontSize={16}>
                  {getProjectActivityMessage(item?.event, '', role)?.message !==
                  ''
                    ? getProjectActivityMessage(item?.event, '', role)?.message
                    : item?.attachment?.body}
                </Text>
              </Box>
            </Box>
            <Box width='100%' style={styles.horizonalLine}></Box>
          </Box>
        ) : (
          <ActivityLogMobile log={item} role={role} />
        )}
      </Box>
    );
  });
};

export const ProjectActivityLog = ({
  data,
}: {
  data: Array<ProjectActivityLogType> | undefined;
}) => {
  const { t } = useTranslation();

  if (data?.length === 0 || !data) {
    return;
  }

  return (
    <Box marginHorizontal='s'>
      <Text
        variant='labelLarge'
        color='textPrimary'
        fontWeight='700'
        fontSize={18}
        marginVertical='s'
        textAlign='left'>
        {t('models:projects.report.overview.taskOverview.projectActivityLog')}
      </Text>
      {/* Header Row */}
      <Box marginHorizontal={Platform.OS === 'web' ? 'xl' : 's'}>
        <Box
          marginHorizontal={Platform.OS !== 'web' ? 'm' : 'none'}
          flexDirection='row'
          justifyContent={
            Platform.OS !== 'web' ? 'space-between' : 'flex-start'
          }
          style={styles.headerRow}>
          <Box style={styles.memberColumn}>
            <Text color='grey04' fontWeight='500' fontSize={10}>
              {t(
                'models:projects.report.overview.taskOverview.member'
              ).toLocaleUpperCase()}
            </Text>
          </Box>
          <Box style={styles.dateColumn}>
            <Text color='grey04' fontWeight='500' fontSize={10}>
              {t(
                'models:projects.report.overview.taskOverview.date'
              ).toLocaleUpperCase()}
            </Text>
          </Box>
          {Platform.OS === 'web' && (
            <Box style={styles.activityColumn}>
              <Text color='grey04' fontWeight='500' fontSize={10}>
                {t(
                  'models:projects.report.overview.taskOverview.activity'
                ).toLocaleUpperCase()}
              </Text>
            </Box>
          )}
        </Box>
      </Box>

      <Box
        marginLeft='s'
        maxWidth={1200}
        width='100%'
        style={styles.horizonalLine}></Box>
      {/* Logs Data */}
      <ActivityLog data={data} />
    </Box>
  );
};

const styles = StyleSheet.create({
  horizonalLine: {
    height: 1,
    alignSelf: Platform.OS === 'web' ? undefined : 'center',
    width:
      Platform.OS === 'web'
        ? Dimensions.get('window').width - 80
        : Dimensions.get('window').width - 60,
    maxWidth: 1200,
    backgroundColor: '#E8E8E8',
  },
  circle: {
    width: 4,
    height: 4,
    borderRadius: 2,
    backgroundColor: '#18A87D',
  },
  headerRow: {
    paddingVertical: 4,
    alignItems: 'center',
  },
  dataRow: {
    flexDirection: 'row',
    paddingVertical: 4,
    alignItems: 'flex-start',
  },
  memberColumn: {
    width: Platform.OS === 'web' ? '25%' : '40%',
    paddingRight: 8,
  },
  dateColumn: {
    width: Platform.OS === 'web' ? '25%' : '20%',
    paddingRight: 8,
  },
  activityColumn: {
    width: '50%',
    flexShrink: 1,
  },
  nameText: {
    flexShrink: 1,
    flexWrap: 'wrap',
  },
});
